import { useMemo } from "react";

import type { HeartRateDataPoint } from "~/api/heartRate";

interface ChartDataPoint {
  x: Date | number;
  y: number;
}

type TimeRange = "1d" | "7d" | "1m" | "6m" | "1y";

interface UseChartDataProps {
  apiData: HeartRateDataPoint[] | null;
  selectedRange: TimeRange;
}

interface UseChartDataReturn {
  filteredData: {
    date: string;
    low: number;
    high: number;
    avg: number;
    hour?: number;
    datetime?: string;
  }[];
  lowChartData: ChartDataPoint[];
  highChartData: ChartDataPoint[];
}

export const useChartData = ({
  apiData,
  selectedRange,
}: UseChartDataProps): UseChartDataReturn => {
  return useMemo(() => {
    if (!apiData || apiData.length === 0) {
      // Return empty arrays when no data
      return {
        filteredData: [],
        lowChartData: [],
        highChartData: [],
      };
    }

    let lowData: ChartDataPoint[] = [];
    let highData: ChartDataPoint[] = [];

    if (selectedRange === "1d") {
      // For hourly data, use the hour as x value
      lowData = apiData.map((item, _index) => ({
        x: new Date(item.date).getHours(),
        y: item.min,
      }));
      highData = apiData.map((item, _index) => ({
        x: new Date(item.date).getHours(),
        y: item.max,
      }));
    } else {
      // For other ranges (week, month, 6m, year), use date as x value
      // Since we only show dates (not hours), we can use the date directly
      lowData = apiData.map((item, _index) => {
        // Extract just the date part from the API response
        const dateString = item.date;
        const [datePart] = dateString.split("T");
        const [year, month, day] = datePart.split("-").map(Number);

        // Create date object (timezone doesn't matter for date-only display)
        const date = new Date(year, month - 1, day);

        return {
          x: date,
          y: item.min,
        };
      });
      highData = apiData.map((item, _index) => {
        // Extract just the date part from the API response
        const dateString = item.date;
        const [datePart] = dateString.split("T");
        const [year, month, day] = datePart.split("-").map(Number);

        // Create date object (timezone doesn't matter for date-only display)
        const date = new Date(year, month - 1, day);

        return {
          x: date,
          y: item.max,
        };
      });
    }

    // Transform API data to match existing interface
    const transformedData = apiData.map((item) => ({
      date: item.date,
      low: item.min,
      high: item.max,
      avg: item.avg,
      hour: selectedRange === "1d" ? new Date(item.date).getHours() : undefined,
      datetime: selectedRange === "1d" ? item.date : undefined,
    }));

    return {
      filteredData: transformedData,
      lowChartData: lowData,
      highChartData: highData,
    };
  }, [apiData, selectedRange]);
};
